import { SkeletonBlock, SkeletonProvider, SkeletonText } from '@protoss/ui';
import * as React from 'react';
import { useLayout } from '../../contexts/LayoutContext';
import { ResizableDivider } from '../ResizableDivider';
import type { RightRailItem } from './AppShell';

export interface RightUtilityPaneProps {
  /** Config-driven menu items - drives header title, content, and optional action */
  items: RightRailItem[];
}

/**
 * RightUtilityPane - resizable utility panel for the active right rail menu
 *
 * Behavior (frontend-architecture.md §10, design requirements):
 * - renders only when rightPaneVisible is true (AppShell gates mounting)
 * - returns a fragment: [ResizableDivider (left side)] + [aside (content pane)]
 * - header shows the active item's label (left) and optional headerAction (right)
 * - content area shows the active item's content
 * - resizable width via the left-side divider
 * - width bounded by --pane-rightutility-width-min and --pane-rightutility-width-max tokens
 * - width persisted to localStorage via LayoutContext
 */
function readPaneConstraints() {
  const root = document.documentElement;
  const style = getComputedStyle(root);
  const minWidth = parseInt(style.getPropertyValue('--pane-rightutility-width-min'), 10);
  // Max is always 50% of the current viewport - computed live on every drag event
  // so it stays accurate after window resize. The CSS token is not used as a hard
  // cap because it would prevent reaching 50vw on wide screens.
  const maxWidth = Math.floor(window.innerWidth * 0.5);
  return {
    minWidth: isNaN(minWidth) ? 240 : minWidth,
    maxWidth,
  };
}

export function RightUtilityPane({ items }: RightUtilityPaneProps) {
  const { rightPaneMode, rightPaneWidth, setRightPaneWidth } = useLayout();

  // Ref to the <aside> element - lets us update width via DOM during drag
  // without triggering React re-renders on every mousemove pixel.
  const asideRef = React.useRef<HTMLElement>(null);

  // Live width during a drag; only synced to React state on mouseup.
  const liveWidthRef = React.useRef<number>(rightPaneWidth);

  // Keep liveWidthRef up to date when state changes externally (e.g. on mount
  // when the persisted value is restored from localStorage).
  React.useEffect(() => {
    liveWidthRef.current = rightPaneWidth;
  }, [rightPaneWidth]);

  // Clamp any persisted width to current viewport constraints.
  // This prevents oversized saved values from hiding the right rail strip.
  const clampWidthToViewport = React.useCallback(() => {
    const { minWidth, maxWidth } = readPaneConstraints();
    const clamped = Math.max(minWidth, Math.min(maxWidth, liveWidthRef.current));
    if (clamped !== liveWidthRef.current) {
      liveWidthRef.current = clamped;
      if (asideRef.current) {
        asideRef.current.style.width = `${clamped}px`;
      }
      setRightPaneWidth(clamped);
    }
  }, [setRightPaneWidth]);

  React.useEffect(() => {
    clampWidthToViewport();
  }, [clampWidthToViewport]);

  React.useEffect(() => {
    const handleResize = () => {
      clampWidthToViewport();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [clampWidthToViewport]);

  // Divider is on the LEFT of this pane:
  //   drag left  (delta < 0) → pane grows  → newWidth = liveWidth - delta (increases)
  //   drag right (delta > 0) → pane shrinks → newWidth = liveWidth - delta (decreases)
  // We mutate the DOM directly here - zero React overhead during drag.
  const handleResize = React.useCallback((delta: number) => {
    const { minWidth, maxWidth } = readPaneConstraints();
    const newWidth = Math.max(minWidth, Math.min(maxWidth, liveWidthRef.current - delta));
    liveWidthRef.current = newWidth;
    if (asideRef.current) {
      asideRef.current.style.width = `${newWidth}px`;
    }
  }, []);

  // On mouseup: commit the width to React state once (persists to localStorage).
  const handleResizeEnd = React.useCallback(() => {
    setRightPaneWidth(liveWidthRef.current);
  }, [setRightPaneWidth]);

  const enabledItems = items.filter((item) => item.enabled !== false);
  const activeItem = enabledItems.find((item) => item.id === rightPaneMode);
  const activeItemLoading = activeItem?.loading ?? false;

  return (
    <>
      <ResizableDivider
        direction="vertical"
        onResize={handleResize}
        onResizeEnd={handleResizeEnd}
        className="shell-rightpane-divider"
        ariaLabel="Resize right pane"
      />
      <aside
        ref={asideRef}
        className="shell-rightpane"
        aria-label="Right utility pane"
        style={{ width: `${rightPaneWidth}px` }}
      >
        {activeItem ? (
          <SkeletonProvider loading={activeItemLoading}>
            <div className="shell-rightpane-header">
              <span className="shell-rightpane-header-title">
                <SkeletonText>{activeItem.headerTitle ?? activeItem.label}</SkeletonText>
              </span>
              {(activeItem.headerAction != null || activeItemLoading) && (
                <div className="shell-rightpane-header-action">
                  {activeItemLoading ? (
                    <SkeletonBlock
                      className="shell-rightpane-header-action-skeleton"
                      width="96px"
                      height="var(--control-height-compact)"
                      radius="var(--radius-sm)"
                    />
                  ) : (
                    activeItem.headerAction
                  )}
                </div>
              )}
            </div>
            {enabledItems.map((item) => (
              <div
                key={item.id}
                className={
                  item.noPadding
                    ? 'shell-rightpane-content shell-rightpane-content--fill'
                    : 'shell-rightpane-content'
                }
                data-mode={item.id}
                hidden={item.id !== activeItem.id}
              >
                <SkeletonProvider loading={item.loading ?? false}>{item.content}</SkeletonProvider>
              </div>
            ))}
          </SkeletonProvider>
        ) : (
          <div className="shell-rightpane-empty">
            <p className="shell-rightpane-empty-text">Select a panel</p>
          </div>
        )}
      </aside>
    </>
  );
}
